/**
 * Purest Mart — game tracking module
 * Page: https://thepurest.co/pages/purest-mart  (SG store, 10.10 Friends & Family)
 *
 * What this does:
 *   1. Sends `game_interaction` events straight to GA4 via gtag (no GTM needed)
 *   2. Writes a `purest_mart` cart attribute so Shopify orders can be attributed to the game
 *
 * Verified on the live page (1 Oct 2026): no GTM container loads here. GA4 runs through the
 * Google tag (GT-5RF9KQR, Shopify Google & YouTube app) with GA4 G-FH0NBW5PNZ on the page.
 *
 * How to use:
 *   - Load this file BEFORE the game's own script (or paste it at the top of the game JS).
 *   - Call PurestMartTracking.<method>() at each moment listed in IMPLEMENTATION.md.
 *   - If the game runs inside an <iframe>, this file must run on the PARENT page.
 *     (It does: templates/page.purest-mart.liquid loads it and relays the game's `track` messages.)
 */
(function (window) {
  'use strict';

  var GA4_ID = 'G-FH0NBW5PNZ'; // SG GA4 property. send_to keeps events out of the other GA4 property on the page
  var GAME_NAME = 'purest_mart';
  var CAMPAIGN = '1010_friends_family';
  var SESSION_KEY = 'pm_tracking_fired';
  var DEBUG = /[?&]pm_debug=1/.test(window.location.search); // add ?pm_debug=1 to the URL to log events

  // gtag stub: if the Google tag hasn't defined gtag yet, queue calls on the same dataLayer it reads from
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function () { window.dataLayer.push(arguments); };
  }

  // ---- once-per-session guard (survives re-renders and page refreshes in the same tab) ----
  var memoryFired = {};

  function readFired() {
    try {
      return JSON.parse(window.sessionStorage.getItem(SESSION_KEY) || '{}');
    } catch (e) {
      return memoryFired;
    }
  }

  function markFired(step) {
    memoryFired[step] = true;
    try {
      var fired = readFired();
      fired[step] = true;
      window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(fired));
    } catch (e) { /* storage blocked: memory guard still applies */ }
  }

  function hasFired(step) {
    return !!(memoryFired[step] || readFired()[step]);
  }

  // ---- core push ----
  var startTime = null;

  function track(step, extra, once) {
    if (once && hasFired(step)) return;
    if (once) markFired(step);

    var payload = {
      send_to: GA4_ID,
      game_name: GAME_NAME,
      game_campaign: CAMPAIGN,
      game_step: step
    };
    if (startTime) payload.seconds_since_play = Math.round((Date.now() - startTime) / 1000);

    if (extra) {
      for (var k in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, k)) payload[k] = extra[k];
      }
    }

    window.gtag('event', 'game_interaction', payload);
    // Same event again under its own name (game_played, game_intro_view…), so each step is a row in
    // GA4's event lists and Realtime without registering game_step as a custom dimension.
    window.gtag('event', 'game_' + step, payload);
    if (DEBUG && window.console) console.log('[PurestMart] game_interaction + game_' + step, payload);
  }

  // ---- Shopify cart attribute (attributes carry through to the order) ----
  // The host page can hand in its own writer so this joins its queue of cart calls instead of racing them.
  var cartWriter = null;

  function tagCart(value) {
    if (DEBUG && window.console) console.log('[PurestMart] cart attribute purest_mart =', value);
    if (cartWriter) return cartWriter(value);
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { purest_mart: value } })
    }).catch(function (err) {
      if (DEBUG && window.console) console.warn('[PurestMart] cart attribute failed', err);
    });
  }

  // ---- public API: one method per moment in the game ----
  window.PurestMartTracking = {
    /** Optional: fn(value) that writes the purest_mart cart attribute */
    setCartWriter: function (fn) { cartWriter = typeof fn === 'function' ? fn : null; },

    /** Intro modal ("Play the sale") is shown */
    introView: function () { track('intro_view', null, true); },

    /** PLAY clicked */
    played: function () {
      startTime = Date.now();
      track('played', null, true);
      tagCart('played');
    },

    /** SKIP TO WEBSITE clicked */
    skipped: function () { track('skipped', null, true); },

    /** X (close) clicked during the game */
    exited: function () { track('exited'); },

    /** A Skincare Trinity item is found. itemNumber = 1, 2 or 3 */
    itemFound: function (itemNumber, itemName) {
      track('item_found_' + itemNumber, { item_number: itemNumber, item_name: itemName || '' }, true);
    },

    /** Bundle price unlocked */
    bundleUnlocked: function () {
      track('bundle_unlocked', null, true);
      tagCart('bundle_unlocked');
    },

    /** Product added to cart inside the game */
    addToCart: function (productTitle, variantId, price, currency) {
      track('add_to_cart', {
        product_title: productTitle || '',
        variant_id: variantId ? String(variantId) : '',
        value: typeof price === 'number' ? price : undefined,
        currency: currency || 'SGD'
      });
    },

    /** Bag icon clicked: opens the bag inside the game */
    openBag: function () { track('open_bag'); },

    /** Player leaves the game for the cart page */
    goToCart: function () { track('go_to_cart'); },

    /** Player leaves the game for checkout */
    goToCheckout: function () { track('go_to_checkout'); },

    /** Sound toggle (optional) */
    soundToggled: function (isOn) { track('sound_toggle', { sound_on: !!isOn }); }
  };
})(window);
